import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAppUser } from './useAppUser';
import { useMealPlanning } from './useMealPlanning';
import { useFirestoreTrigger } from './useFirestoreTrigger';
import {
  clearPlanningLimitsCount,
  loadPlanningLimitsState,
  savePlanningLimitsCount,
} from '../utils/persistentState';
import { MealPlan } from '../types/mealPlan';

const MAX_FUTURE_MEAL_PLANS = 4;

const todayString = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.toISOString().split('T')[0];
};

// This hook is mounted by DesktopNav, MobileNav, and MainDashboard at once,
// and each instance's initial load was fetching the full plans list — three
// identical GET /api/meals/plans per page view. Share one in-flight request
// across instances; the per-instance Firestore listener takes over from its
// first snapshot as before.
let inFlightPlans: { uid: string; promise: Promise<MealPlan[]> } | null = null;

export const usePlanningLimits = () => {
  // useAppUser rather than useAuth: under admin impersonation the merged
  // user's uid is the impersonated user's, whose plans determine the limit.
  const { user, loading: userLoading } = useAppUser();
  const { getUserMealPlans } = useMealPlanning();
  // Seed synchronously from the persisted last-known count so the very first
  // paint already shows the correct nav state — auth hasn't restored yet at
  // this point, so the uid isn't known; the effect below discards the seed
  // if a different user turns out to be signed in.
  const [persistedSeed] = useState(() => loadPlanningLimitsState());
  const [futureMealPlansCount, setFutureMealPlansCount] = useState(persistedSeed?.count ?? 0);
  const [isLoading, setIsLoading] = useState(true);
  // Whether the count has been computed from fresh data (REST or snapshot)
  // this session, as opposed to the persisted seed.
  const hasLiveDataRef = useRef(false);

  const applyFreshCount = useCallback((uid: string, count: number) => {
    hasLiveDataRef.current = true;
    setFutureMealPlansCount(count);
    savePlanningLimitsCount(uid, count);
  }, []);

  // Keyed on the uid, not the merged user object — the object takes a new
  // identity as profile state settles, and each one re-fired the initial-load
  // effect below.
  const uid = user?.uid;

  const fetchPlanningLimits = useCallback(async () => {
    // Don't treat "auth still restoring" as signed out — resetting to 0 here
    // is what made the nav flash the enabled state on page load.
    if (userLoading) {
      return;
    }
    if (!uid) {
      setFutureMealPlansCount(0);
      clearPlanningLimitsCount();
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      let shared = inFlightPlans;
      if (!shared || shared.uid !== uid) {
        const promise = getUserMealPlans();
        shared = { uid, promise };
        inFlightPlans = shared;
        promise.finally(() => {
          if (inFlightPlans === shared) {
            inFlightPlans = null;
          }
        });
      }
      const plans = await shared.promise;

      const todayStr = todayString();

      // Count future meal plans (non-failed plans with start date >= today)
      const futurePlansCount = plans.filter(plan => {
        const isNotFailed = plan.generationStatus.status !== 'failed';
        const isFutureOrToday = plan.startDate >= todayStr;
        return isNotFailed && isFutureOrToday;
      }).length;

      applyFreshCount(uid, futurePlansCount);
    } catch (error) {
      console.error('Error fetching planning limits:', error);
    } finally {
      setIsLoading(false);
    }
  }, [uid, userLoading, getUserMealPlans, applyFreshCount]);

  // Once auth restores, discard the synchronous seed if it belongs to a
  // different user than the one actually signed in.
  useEffect(() => {
    if (!uid || hasLiveDataRef.current) return;
    if (persistedSeed && persistedSeed.uid !== uid) {
      setFutureMealPlansCount(0);
    }
  }, [uid, persistedSeed]);

  // Initial load (also resets to 0 on sign-out); the realtime listener below
  // takes over from its first snapshot.
  useEffect(() => {
    fetchPlanningLimits();
  }, [fetchPlanningLimits]);

  // Keep the count live: watch the user's future meal plan docs so creating,
  // deleting, or a generation failing updates the limit without a page
  // refresh. The snapshot carries startDate and generationStatus, so the
  // count is computed directly from it (failed plans can't be excluded in
  // the query — Firestore allows only one inequality field). Fallback ticks
  // recompute over REST; the cadence is gentle because this hook lives in
  // the always-mounted nav.
  const todayStr = useMemo(() => todayString(), []);
  const futurePlansQuery = useMemo(
    () =>
      uid
        ? query(
            collection(db, 'users', uid, 'meal_plans'),
            where('startDate', '>=', todayStr)
          )
        : null,
    [uid, todayStr]
  );
  useFirestoreTrigger({
    enabled: !!uid,
    target: futurePlansQuery,
    fallbackIntervalMs: 60000,
    onTrigger: (snapshot) => {
      if (snapshot && 'docs' in snapshot && uid) {
        const count = snapshot.docs.filter(
          doc => doc.data().generationStatus?.status !== 'failed'
        ).length;
        applyFreshCount(uid, count);
        setIsLoading(false);
      } else {
        fetchPlanningLimits();
      }
    },
  });

  const isStartPlanningDisabled = futureMealPlansCount >= MAX_FUTURE_MEAL_PLANS;
  const startPlanningTooltipText = isStartPlanningDisabled
    ? `You can have up to ${MAX_FUTURE_MEAL_PLANS} meal plans at once. Wait for some to complete or delete ones you no longer need to create new plans.`
    : undefined;

  return {
    futureMealPlansCount,
    isStartPlanningDisabled,
    startPlanningTooltipText,
    maxFutureMealPlans: MAX_FUTURE_MEAL_PLANS,
    isLoading,
    refetch: fetchPlanningLimits
  };
};
